/**
 * CR310/CR404: S0 Scan Settings v2 - OCR Tab (Tab 2)
 * OCR engine selection, PaddleOCR advanced controls, S1.2 Watchdog + Fallback
 */

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Cpu, ChevronDown, Settings2, Clock, RotateCcw } from 'lucide-react';
import {
  type BatchRunFlyerConfig,
  type OcrEngine,
  type PaddleOcrConfig,
  type S12OcrConfig,
  type S12FallbackOcrConfig,
  type FallbackColorMode,
  PRIMARY_OCR_ENGINES,
  DEFAULT_PADDLE_OCR_CONFIG,
} from '../types';
import { Layers } from 'lucide-react';

interface OcrTabProps {
  config: BatchRunFlyerConfig;
  updateConfig: <K extends keyof BatchRunFlyerConfig>(key: K, value: BatchRunFlyerConfig[K]) => void;
  updatePaddleConfig: <K extends keyof PaddleOcrConfig>(key: K, value: PaddleOcrConfig[K]) => void;
  updateS12OcrConfig: <K extends keyof S12OcrConfig>(key: K, value: S12OcrConfig[K]) => void;
  updateS12FallbackConfig: <K extends keyof S12FallbackOcrConfig>(key: K, value: S12FallbackOcrConfig[K]) => void;
  disabled?: boolean;
}

export function OcrTab({
  config,
  updateConfig,
  updatePaddleConfig,
  updateS12OcrConfig,
  updateS12FallbackConfig,
  disabled = false,
}: OcrTabProps) {
  const isPaddleSelected = config.ocrEngine === 'paddle_ocr' || config.ocrEngine === 'true_paddle';

  return (
    <div className="space-y-6">
      {/* OCR Engine Selection */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Cpu className="h-4 w-4" />
          OCR Engine (Step 1.2)
        </div>

        <Select
          value={config.ocrEngine}
          onValueChange={(v) => updateConfig('ocrEngine', v as OcrEngine)}
          disabled={disabled}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIMARY_OCR_ENGINES.map((engine) => (
              <SelectItem key={engine.value} value={engine.value}>
                <div className="flex flex-col">
                  <span>{engine.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <p className="text-xs text-muted-foreground">
          {PRIMARY_OCR_ENGINES.find(e => e.value === config.ocrEngine)?.description ||
            'Select OCR engine for text extraction'}
        </p>
      </div>

      {/* Min OCR Score */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">Minimum OCR Score</Label>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={0}
            max={100}
            value={config.minOcrScore}
            onChange={(e) => updateConfig('minOcrScore', parseFloat(e.target.value) || 25)}
            disabled={disabled}
            className="h-8 text-xs w-24"
          />
          <span className="text-xs text-muted-foreground">
            Pages below this threshold are flagged as low-quality
          </span>
        </div>
      </div>

      {/* CR404: S1.2 Watchdog Timeout */}
      <div className="space-y-3 pt-2 border-t">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4" />
          S1.2 Watchdog Timeout
        </div>

        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={60}
            max={900}
            step={30}
            value={config.s12Ocr.watchdogTimeoutSec}
            onChange={(e) => updateS12OcrConfig('watchdogTimeoutSec', parseInt(e.target.value) || 300)}
            disabled={disabled}
            className="h-8 text-xs w-24"
          />
          <span className="text-xs text-muted-foreground">
            seconds (default: 300 = 5 min). Pages exceeding this are deferred for fallback.
          </span>
        </div>
      </div>

      <div className="space-y-3 pt-2 border-t">
        <div className="text-sm font-medium">Paddle/Vision OCR Completion Budget</div>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={1024}
            max={16384}
            step={256}
            value={config.s12Ocr.ocrCompletionMaxTokens}
            onChange={(e) => {
              const raw = parseInt(e.target.value) || 8192;
              const clamped = Math.max(1024, Math.min(16384, raw));
              updateS12OcrConfig('ocrCompletionMaxTokens', clamped);
            }}
            disabled={disabled || !isPaddleSelected}
            className="h-8 text-xs w-28"
          />
          <span className="text-xs text-muted-foreground">
            Max OCR completion tokens sent to Paddle/Vision AI (default: 8192, only for Paddle engine).
          </span>
        </div>
      </div>

      <div className="space-y-3 pt-2 border-t">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="text-sm font-medium">S1.2 Fan-Out Execution</div>
            <p className="text-xs text-muted-foreground">
              Use worker fan-out only when OCR page count is large enough.
            </p>
          </div>
          <Switch
            checked={config.s12Ocr.useFanOut}
            onCheckedChange={(v) => updateS12OcrConfig('useFanOut', v)}
            disabled={disabled}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Fan-Out Min Pages</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={1000}
                value={config.s12Ocr.fanOutMinPages}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(1000, parseInt(e.target.value) || 25));
                  updateS12OcrConfig('fanOutMinPages', v);
                }}
                disabled={disabled || !config.s12Ocr.useFanOut}
                className="h-8 text-xs w-24"
              />
              <span className="text-xs text-muted-foreground">
                run inline below this page count
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Fan-Out Workers</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={30}
                value={config.s12Ocr.fanOutConcurrency}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(30, parseInt(e.target.value) || 8));
                  updateS12OcrConfig('fanOutConcurrency', v);
                }}
                disabled={disabled || !config.s12Ocr.useFanOut}
                className="h-8 text-xs w-24"
              />
              <span className="text-xs text-muted-foreground">
                worker invocations in parallel (1–30)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CR404: S1.2 Fallback OCR */}
      <Collapsible defaultOpen={config.s12Ocr.fallback.enabled} className="space-y-2 pt-2 border-t">
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground transition-colors">
            <RotateCcw className="h-4 w-4" />
            <span>Fallback OCR (Retry Deferred Pages)</span>
            <ChevronDown className="h-4 w-4 ml-1 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <Switch
            checked={config.s12Ocr.fallback.enabled}
            onCheckedChange={(v) => updateS12FallbackConfig('enabled', v)}
            disabled={disabled}
          />
        </div>

        <CollapsibleContent className="space-y-4 pt-3">
          {/* Fallback Timeout */}
          <div className="space-y-2">
            <Label className="text-xs">Fallback Timeout (seconds)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={30}
                max={300}
                step={15}
                value={config.s12Ocr.fallback.timeoutSeconds}
                onChange={(e) => updateS12FallbackConfig('timeoutSeconds', parseInt(e.target.value) || 90)}
                disabled={disabled || !config.s12Ocr.fallback.enabled}
                className="h-8 text-xs w-24"
              />
              <span className="text-xs text-muted-foreground">
                Per-page timeout for fallback attempts
              </span>
            </div>
          </div>

          {/* Fallback Render DPI */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Render DPI</Label>
              <Badge variant="outline" className="text-xs font-mono">
                {config.s12Ocr.fallback.renderDpi}
              </Badge>
            </div>
            <Slider
              value={[config.s12Ocr.fallback.renderDpi]}
              onValueChange={([v]) => updateS12FallbackConfig('renderDpi', v)}
              min={72}
              max={300}
              step={18}
              disabled={disabled || !config.s12Ocr.fallback.enabled}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Lower DPI = faster but less detail (72–300)
            </p>
          </div>

          {/* Max Side Px */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Max Side (px)</Label>
              <Badge variant="outline" className="text-xs font-mono">
                {config.s12Ocr.fallback.maxSidePx}
              </Badge>
            </div>
            <Slider
              value={[config.s12Ocr.fallback.maxSidePx]}
              onValueChange={([v]) => updateS12FallbackConfig('maxSidePx', v)}
              min={800}
              max={2400}
              step={100}
              disabled={disabled || !config.s12Ocr.fallback.enabled}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Max dimension for fallback renders (800–2400)
            </p>
          </div>

          {/* Color Mode */}
          <div className="space-y-2">
            <Label className="text-xs">Color Mode</Label>
            <Select
              value={config.s12Ocr.fallback.colorMode}
              onValueChange={(v) => updateS12FallbackConfig('colorMode', v as FallbackColorMode)}
              disabled={disabled || !config.s12Ocr.fallback.enabled}
            >
              <SelectTrigger className="h-8 text-xs w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ORIGINAL">Original</SelectItem>
                <SelectItem value="GRAYSCALE">Grayscale</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Grayscale is faster and often improves OCR accuracy
            </p>
          </div>

          {/* Preprocessing Toggles */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Binarize</Label>
              <Switch
                checked={config.s12Ocr.fallback.binarizeEnabled}
                onCheckedChange={(v) => updateS12FallbackConfig('binarizeEnabled', v)}
                disabled={disabled || !config.s12Ocr.fallback.enabled}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Denoise</Label>
              <Switch
                checked={config.s12Ocr.fallback.denoiseEnabled}
                onCheckedChange={(v) => updateS12FallbackConfig('denoiseEnabled', v)}
                disabled={disabled || !config.s12Ocr.fallback.enabled}
              />
            </div>
          </div>

          {/* Effective Fallback Config */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <Label className="text-xs font-medium">Effective Fallback Config</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
              <div className="flex justify-between">
                <span className="text-muted-foreground">enabled:</span>
                <span>{config.s12Ocr.fallback.enabled ? 'true' : 'false'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">timeout:</span>
                <span>{config.s12Ocr.fallback.timeoutSeconds}s</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">renderDpi:</span>
                <span>{config.s12Ocr.fallback.renderDpi}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">maxSidePx:</span>
                <span>{config.s12Ocr.fallback.maxSidePx}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">colorMode:</span>
                <span>{config.s12Ocr.fallback.colorMode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">binarize:</span>
                <span>{config.s12Ocr.fallback.binarizeEnabled ? 'true' : 'false'}</span>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* CR414: Flyer OCR Concurrency Controls */}
      <div className="space-y-3 pt-2 border-t">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Layers className="h-4 w-4" />
          Flyer Concurrency (Step 1.1 / 1.2)
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Render Concurrency (S1.1)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={10}
                value={config.renderConcurrency}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(10, parseInt(e.target.value) || 3));
                  updateConfig('renderConcurrency', v);
                }}
                disabled={disabled}
                className="h-8 text-xs w-20"
              />
              <span className="text-xs text-muted-foreground">
                pages rendered in parallel (1–10)
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">OCR Concurrency (S1.2)</Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                max={10}
                value={config.ocrConcurrency}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(10, parseInt(e.target.value) || 3));
                  updateConfig('ocrConcurrency', v);
                }}
                disabled={disabled}
                className="h-8 text-xs w-20"
              />
              <span className="text-xs text-muted-foreground">
                pages OCR'd in parallel (1–10)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* PaddleOCR Advanced Controls */}
      {isPaddleSelected && (
        <Collapsible defaultOpen className="space-y-2 pt-2 border-t">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-foreground transition-colors w-full">
            <Settings2 className="h-4 w-4" />
            <span>PaddleOCR Advanced (Signals Mode)</span>
            <ChevronDown className="h-4 w-4 ml-auto transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>

          <CollapsibleContent className="space-y-4 pt-3">
            {/* Angle Correction */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Angle Correction</Label>
                <p className="text-xs text-muted-foreground">
                  Enable text angle classification (slower but handles rotated text)
                </p>
              </div>
              <Switch
                checked={config.paddleOcr.use_angle_cls}
                onCheckedChange={(v) => updatePaddleConfig('use_angle_cls', v)}
                disabled={disabled}
              />
            </div>

            {/* Max Image Side */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Max Image Side (px)</Label>
                <Badge variant="outline" className="text-xs font-mono">
                  {config.paddleOcr.det_limit_side_len}
                </Badge>
              </div>
              <Slider
                value={[config.paddleOcr.det_limit_side_len]}
                onValueChange={([v]) => updatePaddleConfig('det_limit_side_len', v)}
                min={640}
                max={2048}
                step={64}
                disabled={disabled}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Larger values increase accuracy but use more memory (640–2048)
              </p>
            </div>

            {/* Limit Mode */}
            <div className="space-y-2">
              <Label className="text-xs">Limit Mode</Label>
              <Select
                value={config.paddleOcr.det_limit_type}
                onValueChange={(v) => updatePaddleConfig('det_limit_type', v as 'max' | 'min')}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 text-xs w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="max">Max (limit longest edge)</SelectItem>
                  <SelectItem value="min">Min (limit shortest edge)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Min Confidence (drop_score) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Min Confidence</Label>
                <Badge variant="outline" className="text-xs font-mono">
                  {config.paddleOcr.drop_score.toFixed(2)}
                </Badge>
              </div>
              <Slider
                value={[config.paddleOcr.drop_score]}
                onValueChange={([v]) => updatePaddleConfig('drop_score', v)}
                min={0.1}
                max={0.9}
                step={0.05}
                disabled={disabled}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Text boxes below this confidence are discarded (0.1–0.9)
              </p>
            </div>

            {/* Effective Config Receipt */}
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <Label className="text-xs font-medium">Effective OCR Config</Label>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-mono">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">engine:</span>
                  <span>{config.ocrEngine}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">use_angle_cls:</span>
                  <span>{config.paddleOcr.use_angle_cls ? 'true' : 'false'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">det_limit_type:</span>
                  <span>{config.paddleOcr.det_limit_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">det_limit_side_len:</span>
                  <span>{config.paddleOcr.det_limit_side_len}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">drop_score:</span>
                  <span>{config.paddleOcr.drop_score}</span>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
